<template>
  <v-container>
    <div class="d-flex justify-end mb-2">
      <v-btn color="primary" @click="showCreateModal"><v-icon left>mdi-book-plus</v-icon>{{ trans('misc.newMiscObject') }}</v-btn>
    </div>

    <ajax-table
      ref="objectMiscsTable"
      :resource="resourceUrl + '/object'"
      :headers="[
        {text: 'misc.settleDone', value: 'settleDone', width: 90},
        {text: 'misc.object', value: 'object.name'},
        {text: 'misc.settleDate', value: 'settleDate'},
        {text: 'misc.description', value: 'description'},
        {text: 'misc.account', value: 'account.accountNbr'},
        {text: 'misc.receiptField', value: 'receiptField'},
        {text: 'misc.price', value: 'price', type: 'currency'},
        {text: 'misc.tax', value: 'tax', type: 'currency'},
        {text: '', value: 'actions'},
      ]"
      :page-options="{itemsPerPage: 20}"
      :items-per-page-options="[10, 20, 50]"
      filterable
      :filter="filter"
      no-elevation
    >

      <template v-slot:filters>
        <select-object hide-details :object-id.sync="filterData.objectId" style="max-width: 250px" clearable/>
        <select-month hide-details v-model="filterData.month" style="max-width: 150px" class="mr-0" clearable/>
        <select-year hide-details v-model="filterData.year" style="max-width: 100px" class="ml-0" min-year="2012" :max-year="new Date().getFullYear() + 1" clearable/>
        <v-checkbox hide-details :label="trans('misc.filter.settleDone')" v-model="filterData.settleDone"></v-checkbox>
        <search v-model="filterData.description" />
      </template>

      <template v-slot:item.object.name="{item}">
        <object-name :value="item" />
      </template>

      <template v-slot:item.settleDone="{item}">
        <status-badge
            :color="item.settleDone ? 'success' : 'pending'"
        >{{ item.settleDone ? trans('misc.settleDoneTrue') : trans('misc.settleDoneFalse') }}</status-badge>
      </template>

      <template v-slot:item.settleDate="{item}">
        <div v-if="item.settleDate" class="mt-2 mb-1">{{ formatMonth(item.settleDate) }}</div>
      </template>

      <template v-slot:item.account.accountNbr="{item}">
        <div v-if="item.account" class="mt-2 mb-1">{{ item.account.accountNbr }}</div>
      </template>

      <template v-slot:item.actions="{ item }">
        <context-menu-icon>
          <v-list dense>
            <v-list-item @click="showUpdateModal(item)">
              <v-list-item-icon><v-icon>mdi-pencil</v-icon></v-list-item-icon>
              <v-list-item-title>{{ trans('misc.editMiscObject') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="objectMiscToDelete = item">
              <v-list-item-icon><v-icon>mdi-delete</v-icon></v-list-item-icon>
              <v-list-item-title>{{ trans('misc.deleteMiscObject') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </context-menu-icon>
      </template>

    </ajax-table>

    <object-misc-addit-modal :initial-object-misc="objectMiscToUpdate" :show-modal.sync="showMiscCreateModal" @success="$refs.objectMiscsTable.loadData()"/>

    <confirm-deletion-modal
        :title="trans('misc.deleteMiscObject')"
        :show-modal="objectMiscToDelete != null"
        @confirm="deleteObjectMisc"
        @close="objectMiscToDelete = null"
    >
      <template v-if="objectMiscToDelete">
        <b>{{ trans('misc.description') }}: </b>
        {{ objectMiscToDelete.description }}
        <b>{{ trans('misc.price') }}: </b>
        <currency-amount style="display: inline-block" :amount="objectMiscToDelete.price" currency="EUR"/>
      </template>
    </confirm-deletion-modal>

  </v-container>
</template>

<script>
import AjaxTable from './AjaxTable';
import {LocaleUtils} from '../utils/locale-utils';
import StatusBadge from './StatusBadge';
import ContextMenuIcon from './ContextMenuIcon';
import {DateUtils} from '../utils/date-utils';
import {RouterUtils} from '../utils/router-utils';
import {MainUtils} from '@/utils/main-utils';
import ObjectMiscAdditModal from '@/components/ObjectMiscAdditModal';
import dayjs from 'dayjs';
import ConfirmDeletionModal from '@/components/ConfirmDeletionModal';
import CurrencyAmount from '@/components/CurrencyAmount';
import {MiscApi} from '@/api/misc-api'
import {and, comparison, eq, or} from 'rsql-builder';
import {like} from '@/lib/custom-rsql-operators';
import Search from '@/components/Search';
import SelectMonth from '@/components/SelectMonth';
import SelectYear from '@/components/SelectYear';
import SelectObject from '@/components/SelectObject';
import ObjectName from '@/components/ObjectName';
import {FilterUtils} from '@/utils/filter-utils';

export default {
  name: 'ObjectsMiscs',
  mixins: [
    LocaleUtils,
    DateUtils,
    RouterUtils,
    MainUtils,
    FilterUtils
  ],
  components: {
    ObjectName,
    SelectObject,
    SelectYear,
    SelectMonth,
    Search,
    CurrencyAmount,
    ConfirmDeletionModal,
    ObjectMiscAdditModal,
    AjaxTable,
    StatusBadge,
    ContextMenuIcon
  },
  data: () => ({
    filterData: {
      settleDone: null,
      description: null,
      month: null,
      year: null,
      objectId: null,
    },
    showMiscCreateModal: false,
    objectMiscToUpdate: null,
    objectMiscToDelete: null
  }),
  computed: {
    filter () {
      let filters =  [];
      if(this.filterData.settleDone) {
        filters.push(comparison('settleDone', eq(0)))
      }
      if(this.filterData.description) {
        filters.push(this.buildSearchFilter(this.filterData.description, ['description', 'receiptField', 'account.accountNbr']));
      }
      if(this.filterData.month && this.filterData.year) {
        filters.push(comparison('settleDate', eq(this.filterData.year + '-' + this.filterData.month)))
      }
      if(this.filterData.objectId != null) {
        filters.push(comparison('object.id', eq(this.filterData.objectId)))
      }
      return filters;
    },
    resourceUrl () {
      return MiscApi.apiUri;
    }
  },
  activated () {
    this.$refs.objectMiscsTable.loadData();
  },
  methods: {
    formatMonth(isoMonth) {
      return dayjs(isoMonth).format('MMMM YYYY');
    },
    showCreateModal() {
      this.objectMiscToUpdate = null;
      this.showMiscCreateModal = true;
    },
    showUpdateModal(objectMisc) {
      this.objectMiscToUpdate = objectMisc;
      this.showMiscCreateModal = true;
    },
    deleteObjectMisc() {
      MiscApi.deleteObjectMisc(this.objectMiscToDelete.miscId)
        .then(response => {
          this.$refs.objectMiscsTable.loadData();
        })
        .catch(this.handleErrorResponse);
    }
  }
}
</script>

<style>
</style>
